import React from 'react'
import { useParams } from 'react-router-dom'
import { Box, Container, MenuItem, Grid } from '@mui/material'
import AdminPageHeader from '../../components/admin/shared/AdminPageHeader'
import AdminFormCard from '../../components/admin/shared/AdminFormCard'
import AdminFormActions from '../../components/admin/shared/AdminFormActions'
import AdminFormLoading from '../../components/admin/shared/AdminFormLoading'
import FormErrorSummary from '../../components/admin/shared/FormErrorSummary'
import {
  LabeledSelect,
  LabeledTextField,
  ToggleField,
} from '../../components/admin/shared/fields'
import { ValidationSchema } from '../../hooks/useFormValidation'
import { useSimpleAdminForm } from '../../hooks/useSimpleAdminForm'

interface BDMFormData {
  first_name: string
  last_name: string
  email: string
  phone: string
  market: string
  notes: string
  is_active: boolean
}

const initialFormData: BDMFormData = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  market: 'ANZ',
  notes: '',
  is_active: true
}

const validationSchema: ValidationSchema = {
  first_name: [{ type: 'required', message: 'First name is required' }],
  last_name: [{ type: 'required', message: 'Last name is required' }],
  email: [
    { type: 'required', message: 'Email is required' },
    { type: 'email', message: 'Invalid email address' },
  ],
  market: [{ type: 'required', message: 'Market is required' }]
}

// Friendly labels for the shared validation summary.
const BDM_FIELD_LABELS: Record<string, string> = {
  first_name: 'First Name',
  last_name: 'Last Name',
  email: 'Email',
  market: 'Market',
}

const BDMForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>()
  const isEditMode = !!id

  const {
    values,
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    isSubmitting,
    loading,
    error,
  } = useSimpleAdminForm<BDMFormData>({
    isEditMode,
    resourcePath: '/admin/bdm',
    resourceId: id,
    listPath: '/admin/system/bdm',
    initialValues: initialFormData,
    validationSchema,
    toPayload: (v) => ({
      first_name: v.first_name.trim(),
      last_name: v.last_name.trim(),
      email: v.email.trim().toLowerCase(),
      phone: v.phone.trim() || null,
      market: v.market,
      notes: v.notes.trim() || null,
      is_active: v.is_active,
    }),
    fromResponse: (data) => ({
      first_name: data.first_name || '',
      last_name: data.last_name || '',
      email: data.email || '',
      phone: data.phone || '',
      market: data.market || 'ANZ',
      notes: data.notes || '',
      is_active: data.is_active ?? true,
    }),
    saveErrorMessage: 'Failed to save ROM/FBC',
    loadErrorMessage: 'Failed to load ROM/FBC',
  })

  if (loading) {
    return <AdminFormLoading />
  }

  const bdmName = `${values.first_name} ${values.last_name}`.trim()

  return (
    <Container maxWidth="md">
      <AdminPageHeader
        pageKey="bdm"
        dense
        titleOverride={isEditMode ? 'Edit ROM/FBC' : 'Add ROM/FBC'}
        breadcrumbs={[
          { label: 'Admin', to: '/admin' },
          { label: 'System', to: '/admin/system' },
          { label: 'ROM/FBC Management', to: '/admin/system/bdm' },
          ...(isEditMode ? [{ label: bdmName || 'ROM/FBC' }, { label: 'Edit' }] : [{ label: 'Add New' }]),
        ]}
      />

      <FormErrorSummary
        message={error}
        errors={errors as Record<string, string>}
        fieldLabels={BDM_FIELD_LABELS}
      />

      <AdminFormCard title="ROM/FBC details">
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <LabeledTextField
              label="First Name"
              required
              value={values.first_name}
              onChange={handleChange('first_name')}
              onBlur={handleBlur('first_name')}
              error={!!errors.first_name}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <LabeledTextField
              label="Last Name"
              required
              value={values.last_name}
              onChange={handleChange('last_name')}
              onBlur={handleBlur('last_name')}
              error={!!errors.last_name}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <LabeledTextField
              label="Email"
              required
              type="email"
              value={values.email}
              onChange={handleChange('email')}
              onBlur={handleBlur('email')}
              error={!!errors.email}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <LabeledTextField
              label="Phone"
              value={values.phone}
              onChange={handleChange('phone')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <LabeledSelect
              label="Market"
              required
              value={values.market}
              onChange={handleChange('market')}
              error={!!errors.market}
            >
              <MenuItem value="ANZ">ANZ</MenuItem>
              <MenuItem value="USA">USA</MenuItem>
            </LabeledSelect>
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'flex-end' }}>
            <Box sx={{ width: '100%' }}>
              <ToggleField
                label="Active"
                description="Inactive ROM/FBCs are hidden from assignment lists."
                checked={values.is_active}
                onChange={(checked) => setFieldValue('is_active', checked)}
                activeColor="success"
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <LabeledTextField
              label="Notes"
              multiline
              rows={3}
              value={values.notes}
              onChange={handleChange('notes')}
              placeholder="Optional notes about this ROM/FBC..."
            />
          </Grid>
        </Grid>
      </AdminFormCard>

      <AdminFormActions
        saveLabel={isEditMode ? 'Save Changes' : 'Create ROM/FBC'}
        saving={isSubmitting}
        onSave={() => handleSubmit()}
        cancelTo="/admin/system/bdm"
      />
    </Container>
  )
}

export default BDMForm
